import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Search,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  UserCheck,
  Users,
  Shield,
  Loader2,
  RefreshCw,
  ClipboardCheck,
  FileEdit,
  GraduationCap,
  Plane,
  Ticket,
  Home,
  Wallet,
  Briefcase,
  ThumbsUp,
  ThumbsDown,
  Eye,
  Calendar,
  ArrowRight
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

// Status configuration
const APPROVAL_STATUS_CONFIG = {
  PENDING_APPROVAL: {
    label: 'Pending Approval',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: Clock
  },
  APPROVED: {
    label: 'Approved',
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: CheckCircle
  },
  REJECTED: {
    label: 'Rejected',
    color: 'bg-red-100 text-red-700 border-red-200',
    icon: XCircle
  }
};

// Service types configuration
const SERVICE_CONFIG = {
  PROFILE_ASSESSMENT: { name: 'Profile Assessment', icon: ClipboardCheck, color: 'text-blue-600 bg-blue-50' },
  UNIVERSITY_SHORTLISTING: { name: 'Preapplication Support', icon: GraduationCap, color: 'text-green-600 bg-green-50' },
  APPLICATION_ASSISTANCE: { name: 'Apply University', icon: FileEdit, color: 'text-purple-600 bg-purple-50' },
  VISA_GUIDANCE: { name: 'Visa & Interview Support', icon: Plane, color: 'text-yellow-600 bg-yellow-50' },
  SCHOLARSHIP_SEARCH: { name: 'Ticket & Travel Support', icon: Briefcase, color: 'text-cyan-600 bg-cyan-50' },
  ACCOMMODATION_HELP: { name: 'Find Accommodation', icon: Home, color: 'text-pink-600 bg-pink-50' },
  LOAN_ASSISTANCE: { name: 'Education Loan', icon: Wallet, color: 'text-emerald-600 bg-emerald-50' },
  PRE_DEPARTURE_ORIENTATION: { name: 'Find Jobs Abroad', icon: Ticket, color: 'text-orange-600 bg-orange-50' },
};

export default function AdminAgentRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [statusFilter, setStatusFilter] = useState('PENDING_APPROVAL');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });

  // Dialog states
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [statusFilter, page]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAgentRequests({
        status: statusFilter,
        page,
        limit: 20
      });
      setRequests(response.requests || []);
      setCounts(response.counts || { pending: 0, approved: 0, rejected: 0 });
      setPagination(response.pagination || { total: 0, totalPages: 1 });
    } catch (error) {
      console.error('Failed to fetch agent requests:', error);
      toast.error('Failed to load agent requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    try {
      setProcessing(true);
      await adminAPI.approveAgentRequest(selectedRequest.serviceRequestId, approvalNotes);
      toast.success('Service request approved successfully');
      setApproveDialogOpen(false);
      setApprovalNotes('');
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      console.error('Failed to approve request:', error);
      toast.error(error.response?.data?.error || 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      setProcessing(true);
      await adminAPI.rejectAgentRequest(selectedRequest.serviceRequestId, rejectionReason);
      toast.success('Service request rejected');
      setRejectDialogOpen(false);
      setRejectionReason('');
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      console.error('Failed to reject request:', error);
      toast.error(error.response?.data?.error || 'Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  const openApproveDialog = (request) => {
    setSelectedRequest(request);
    setApprovalNotes('');
    setApproveDialogOpen(true);
  };

  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const openDetailsDialog = (request) => {
    setSelectedRequest(request);
    setDetailsDialogOpen(true);
  };

  const getServiceConfig = (serviceType) => {
    return SERVICE_CONFIG[serviceType] || {
      name: serviceType?.replace(/_/g, ' ') || 'Unknown',
      icon: ClipboardCheck,
      color: 'text-gray-600 bg-stone-50/80'
    };
  };

  const filteredRequests = requests.filter(request => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const studentName = `${request.student?.firstName || ''} ${request.student?.lastName || ''}`.toLowerCase();
    const agentName = `${request.agent?.firstName || ''} ${request.agent?.lastName || ''}`.toLowerCase();
    const serviceName = getServiceConfig(request.serviceType).name.toLowerCase();
    return studentName.includes(query) || agentName.includes(query) || serviceName.includes(query);
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">Agent Service Requests</h1>
            <p className="text-gray-500 text-sm mt-1">
              Review and approve service requests submitted by agents for their students
            </p>
          </div>
          <Button
            onClick={fetchRequests}
            variant="outline"
            className="gap-2"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card
            className={`p-5 cursor-pointer transition-all border-2 bg-white ${
              statusFilter === 'PENDING_APPROVAL'
                ? 'border-amber-400 bg-amber-50'
                : 'border-transparent hover:border-amber-200'
            }`}
            onClick={() => { setStatusFilter('PENDING_APPROVAL'); setPage(1); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Approval</p>
                <p className="text-3xl font-bold text-amber-600">{counts.pending}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-xl">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </Card>

          <Card
            className={`p-5 cursor-pointer transition-all border-2 bg-white ${
              statusFilter === 'APPROVED'
                ? 'border-green-400 bg-green-50'
                : 'border-transparent hover:border-green-200'
            }`}
            onClick={() => { setStatusFilter('APPROVED'); setPage(1); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Approved</p>
                <p className="text-3xl font-bold text-green-600">{counts.approved}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>

          <Card
            className={`p-5 cursor-pointer transition-all border-2 bg-white ${
              statusFilter === 'REJECTED'
                ? 'border-red-400 bg-red-50'
                : 'border-transparent hover:border-red-200'
            }`}
            onClick={() => { setStatusFilter('REJECTED'); setPage(1); }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Rejected</p>
                <p className="text-3xl font-bold text-red-600">{counts.rejected}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-xl">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="p-4 bg-white border-stone-200/80">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by student, agent, or service..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="PENDING_APPROVAL">Pending Approval</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Requests Table */}
        <Card className="bg-white border-stone-200/80 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#F2575F]" />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No agent requests found</p>
              <p className="text-sm text-gray-400 mt-1">
                {statusFilter === 'PENDING_APPROVAL'
                  ? 'No pending requests to review'
                  : 'Try adjusting your filters'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50/80">
                  <TableHead className="font-semibold">Student</TableHead>
                  <TableHead className="font-semibold">Agent</TableHead>
                  <TableHead className="font-semibold">Service</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Requested</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map((request) => {
                  const serviceConfig = getServiceConfig(request.serviceType);
                  const ServiceIcon = serviceConfig.icon;
                  const statusConfig = APPROVAL_STATUS_CONFIG[request.agentApprovalStatus] || APPROVAL_STATUS_CONFIG.PENDING_APPROVAL;
                  const StatusIcon = statusConfig.icon;

                  return (
                    <TableRow key={request.serviceRequestId} className="hover:bg-stone-50/80">
                      {/* Student */}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarImage src={request.student?.avatar} />
                            <AvatarFallback className="bg-emerald-100 text-emerald-700 text-sm">
                              {request.student?.firstName?.[0]}{request.student?.lastName?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-gray-900">
                              {request.student?.firstName} {request.student?.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{request.student?.email}</p>
                          </div>
                        </div>
                      </TableCell>

                      {/* Agent */}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarImage src={request.agent?.avatar} />
                            <AvatarFallback className="bg-blue-100 text-blue-700 text-sm">
                              {request.agent?.firstName?.[0]}{request.agent?.lastName?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-gray-900">
                              {request.agent?.firstName} {request.agent?.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{request.agent?.email}</p>
                          </div>
                        </div>
                      </TableCell>

                      {/* Service */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`p-2 rounded-lg ${serviceConfig.color}`}>
                            <ServiceIcon className="w-4 h-4" />
                          </div>
                          <span className="text-sm font-medium">{serviceConfig.name}</span>
                        </div>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Badge variant="outline" className={`gap-1 ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </Badge>
                      </TableCell>

                      {/* Requested Date */}
                      <TableCell>
                        <div className="text-sm">
                          <p className="text-gray-900">
                            {request.appliedAt ? format(new Date(request.appliedAt), 'MMM d, yyyy') : '-'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {request.appliedAt ? formatDistanceToNow(new Date(request.appliedAt), { addSuffix: true }) : ''}
                          </p>
                        </div>
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDetailsDialog(request)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {request.agentApprovalStatus === 'PENDING_APPROVAL' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => openApproveDialog(request)}
                                className="bg-green-500 hover:bg-green-600 text-white gap-1"
                              >
                                <ThumbsUp className="w-4 h-4" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openRejectDialog(request)}
                                className="text-red-600 border-red-200 hover:bg-red-50 gap-1"
                              >
                                <ThumbsDown className="w-4 h-4" />
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-gray-500">
                Showing {filteredRequests.length} of {pagination.total} requests
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Approve Dialog */}
        <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                Approve Service Request
              </DialogTitle>
              <DialogDescription>
                This will allow the agent to access the service workflow and manage tasks for this student.
              </DialogDescription>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-stone-50/80 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Student</span>
                    <span className="font-medium">
                      {selectedRequest.student?.firstName} {selectedRequest.student?.lastName}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Agent</span>
                    <span className="font-medium">
                      {selectedRequest.agent?.firstName} {selectedRequest.agent?.lastName}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Service</span>
                    <span className="font-medium">
                      {getServiceConfig(selectedRequest.serviceType).name}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Approval Notes (Optional)
                  </label>
                  <Textarea
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                    placeholder="Add any notes for this approval..."
                    rows={3}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setApproveDialogOpen(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleApprove}
                disabled={processing}
                className="bg-green-500 hover:bg-green-600 gap-2"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Approving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Approve Request
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <XCircle className="w-5 h-5" />
                Reject Service Request
              </DialogTitle>
              <DialogDescription>
                The agent will be notified that this request has been rejected and will not be able to access the service workflow.
              </DialogDescription>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-stone-50/80 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Student</span>
                    <span className="font-medium">
                      {selectedRequest.student?.firstName} {selectedRequest.student?.lastName}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Agent</span>
                    <span className="font-medium">
                      {selectedRequest.agent?.firstName} {selectedRequest.agent?.lastName}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Service</span>
                    <span className="font-medium">
                      {getServiceConfig(selectedRequest.serviceType).name}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Rejection Reason <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a reason for rejection..."
                    rows={3}
                    className={!rejectionReason.trim() ? 'border-red-200' : ''}
                  />
                  {!rejectionReason.trim() && (
                    <p className="text-xs text-red-500 mt-1">Rejection reason is required</p>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setRejectDialogOpen(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                disabled={processing || !rejectionReason.trim()}
                variant="destructive"
                className="gap-2"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Rejecting...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    Reject Request
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Details Dialog */}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Request Details</DialogTitle>
            </DialogHeader>

            {selectedRequest && (
              <div className="space-y-6 py-4">
                {/* Student Info */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Student Information
                  </h4>
                  <div className="flex items-center gap-4 p-4 bg-stone-50/80 rounded-lg">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={selectedRequest.student?.avatar} />
                      <AvatarFallback className="bg-emerald-100 text-emerald-700">
                        {selectedRequest.student?.firstName?.[0]}{selectedRequest.student?.lastName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {selectedRequest.student?.firstName} {selectedRequest.student?.lastName}
                      </p>
                      <p className="text-sm text-gray-500">{selectedRequest.student?.email}</p>
                      {selectedRequest.student?.institution && (
                        <p className="text-xs text-gray-400 mt-1">{selectedRequest.student?.institution}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Agent Info */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <UserCheck className="w-4 h-4" />
                    Agent Information
                  </h4>
                  <div className="flex items-center gap-4 p-4 bg-stone-50/80 rounded-lg">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={selectedRequest.agent?.avatar} />
                      <AvatarFallback className="bg-blue-100 text-blue-700">
                        {selectedRequest.agent?.firstName?.[0]}{selectedRequest.agent?.lastName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {selectedRequest.agent?.firstName} {selectedRequest.agent?.lastName}
                      </p>
                      <p className="text-sm text-gray-500">{selectedRequest.agent?.email}</p>
                    </div>
                  </div>
                </div>

                {/* Service Info */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Service Details
                  </h4>
                  <div className="p-4 bg-stone-50/80 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Service Type</span>
                      <span className="font-medium">
                        {getServiceConfig(selectedRequest.serviceType).name}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Approval Status</span>
                      <Badge variant="outline" className={APPROVAL_STATUS_CONFIG[selectedRequest.agentApprovalStatus]?.color}>
                        {APPROVAL_STATUS_CONFIG[selectedRequest.agentApprovalStatus]?.label}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Requested On</span>
                      <span className="font-medium">
                        {selectedRequest.appliedAt ? format(new Date(selectedRequest.appliedAt), 'PPp') : '-'}
                      </span>
                    </div>
                    {selectedRequest.approvedAt && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Approved On</span>
                        <span className="font-medium">
                          {format(new Date(selectedRequest.approvedAt), 'PPp')}
                        </span>
                      </div>
                    )}
                    {selectedRequest.rejectedAt && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Rejected On</span>
                        <span className="font-medium">
                          {format(new Date(selectedRequest.rejectedAt), 'PPp')}
                        </span>
                      </div>
                    )}
                    {selectedRequest.approvalNotes && (
                      <div className="pt-2 border-t">
                        <span className="text-sm text-gray-500 block mb-1">Notes</span>
                        <p className="text-sm text-gray-700">{selectedRequest.approvalNotes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
                Close
              </Button>
              {selectedRequest?.agentApprovalStatus === 'PENDING_APPROVAL' && (
                <Button
                  onClick={() => {
                    setDetailsDialogOpen(false);
                    openApproveDialog(selectedRequest);
                  }}
                  className="bg-green-500 hover:bg-green-600 gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Approve
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
